import { useState } from "react";
import { Loader2, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";

/**
 * Feb-2026 (User-Wunsch) — „Als Vorlage speichern".
 *
 * Speichert die aktuellen Modell-/Stammdaten als wiederverwendbare Vorlage.
 * Fahrzeug-spezifische Daten (FIN, Kennzeichen, Erstzulassung, Kilometerstand,
 * Zustand, Vorbesitzer, Preise, Disposition, Notizen, Bilder) werden bewusst
 * NICHT gespeichert — die Vorlage erscheint anschließend in „Letzte 5 Vorlagen".
 */
const WIPED_LABELS = [
    "FIN & Kennzeichen", "Erstzulassung & Kilometerstand", "Zustand",
    "Vorbesitzer", "Preise & Provision", "Disposition & Papiere",
    "Notizen & Bemerkungen", "Bilder & Dokumente",
];

export default function SaveAsTemplateDialog({ open, onOpenChange, form, onSaved, published = false }) {
    const [saving, setSaving] = useState(false);
    const brand = (form?.brand || "").trim();
    const model = (form?.model || "").trim();
    const canSave = !!brand && !!model;

    const save = async () => {
        if (!canSave) {
            toast.error("Bitte zuerst Marke und Modell ausfüllen.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/listings/templates", { form });
            onOpenChange(false);
            toast.success(`Vorlage „${brand} ${model}“ gespeichert — abrufbar unter „Vorlage übernehmen“.`, { duration: 6000 });
            onSaved?.(data?.template);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vorlage konnte nicht gespeichert werden.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm" data-testid="save-template-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
                        {published
                            ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            : <Save className="w-5 h-5 text-[#111827]" />}
                        {published ? "Inserat veröffentlicht — als Vorlage speichern?" : "Als Vorlage speichern"}
                    </DialogTitle>
                    <DialogDescription>
                        {!canSave
                            ? <span className="text-red-600 font-medium">Bitte zuerst Marke und Modell ausfüllen.</span>
                            : published
                                ? <>Möchtest du die Stammdaten von <span className="font-bold text-[#111827]">{brand} {model}</span> als wiederverwendbare Vorlage speichern? Das beschleunigt künftige Serien-Inserate.</>
                                : <>Die Stammdaten von <span className="font-bold text-[#111827]">{brand} {model}</span> werden als wiederverwendbare Vorlage gespeichert.</>}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid="save-template-wiped-info">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Diese fahrzeugspezifischen Daten werden NICHT übernommen:
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                        {WIPED_LABELS.map((l) => (
                            <li key={l} className="text-[11px] text-amber-900 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0" /> {l}
                            </li>
                        ))}
                    </ul>
                    <p className="text-[10px] text-amber-700 mt-2">
                        Übernommen werden Marke, Modell, Hersteller, Motor-/Antriebsdaten, Farbe, Ausstattung, Standort u. ä.
                    </p>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="rounded-sm"
                        data-testid="save-template-cancel-btn"
                    >
                        {published ? "Nein, danke" : "Abbrechen"}
                    </Button>
                    <Button
                        type="button"
                        onClick={save}
                        disabled={saving || !canSave}
                        className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm"
                        data-testid="save-template-confirm-btn"
                    >
                        {saving
                            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Speichere…</>
                            : <><Save className="w-4 h-4 mr-1.5" /> Als Vorlage speichern</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
